import { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import api, { formatError } from "../lib/api";
import { UploadCloud, Trash2, FileText, Info, Loader2 } from "lucide-react";

const human = (b) => {
  if (b < 1024) return `${b} o`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} Ko`;
  return `${(b / 1024 / 1024).toFixed(1)} Mo`;
};

export default function LibraryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const fetchItems = async () => {
    try {
      const { data } = await api.get("/examples");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const uploadFile = async (file) => {
    setErr("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/examples", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await fetchItems();
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setUploading(false);
    }
  };

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    for (const f of files) await uploadFile(f);
    e.target.value = "";
  };

  const onDrop = async (e) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    for (const f of files) await uploadFile(f);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cet exemple de la bibliothèque ?")) return;
    try {
      await api.delete(`/examples/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) {
      setErr(formatError(e));
    }
  };

  return (
    <AppShell>
      <div className="px-8 lg:px-12 py-10 max-w-[1400px]" data-testid="library-page">
        <div className="label-xs mb-2">Bibliothèque d'exemples</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Exemples de rattrapages passés
        </h1>
        <p className="text-slate-600 mt-2 text-sm max-w-2xl">
          Déposez ici les anciens sujets de rattrapage QCM. L'IA s'en servira comme référence pour calibrer le style,
          le format et le niveau de difficulté de tous les nouveaux sujets générés.
        </p>

        <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 bg-blue-50/60 border border-blue-100 px-3 py-2 rounded-sm max-w-2xl">
          <Info size={14} className="text-[#2563eb] mt-0.5 shrink-0" />
          <span>Une seule étape — ces exemples seront réutilisés automatiquement à chaque génération.</span>
        </div>

        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`mt-8 upload-zone ${dragging ? "dragging" : ""} rounded-md p-10 text-center cursor-pointer`}
          onClick={() => inputRef.current?.click()}
          data-testid="library-upload-zone"
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept=".pdf,.docx,.txt,.md"
            className="hidden"
            onChange={onPick}
            data-testid="library-file-input"
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-3 text-slate-700">
              <Loader2 className="animate-spin" />
              <div className="text-sm">Upload en cours…</div>
            </div>
          ) : (
            <>
              <UploadCloud className="mx-auto text-slate-400 mb-3" />
              <div className="font-semibold text-slate-900 text-sm">Glissez-déposez des fichiers ici</div>
              <div className="text-xs text-slate-500 mt-1">PDF, DOCX, TXT — 25 Mo max par fichier</div>
              <button
                type="button"
                className="mt-4 inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-900 px-3 py-1.5 rounded-sm text-xs font-semibold hover:bg-slate-50"
              >
                Parcourir mes fichiers
              </button>
            </>
          )}
        </div>

        {err && (
          <div className="mt-4 text-sm text-[#b91c1c] bg-[#fef2f2] border border-red-200 px-3 py-2 rounded-sm" data-testid="library-error">
            {err}
          </div>
        )}

        <div className="mt-10">
          <div className="label-xs mb-3">Bibliothèque ({items.length})</div>
          {loading ? (
            <div className="text-sm text-slate-500">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" data-testid="library-empty">
              Aucun exemple chargé pour l'instant.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {items.map((it) => (
                <div key={it.id} className="bg-white border border-slate-200 rounded-md p-4 flex items-start gap-3" data-testid={`example-${it.id}`}>
                  <div className="w-9 h-9 rounded-sm bg-slate-100 flex items-center justify-center shrink-0">
                    <FileText size={16} className="text-slate-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-900 truncate">{it.original_filename}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {human(it.size || 0)} · {new Date(it.created_at).toLocaleDateString("fr-FR")}
                    </div>
                  </div>
                  <button
                    onClick={() => onDelete(it.id)}
                    className="text-slate-400 hover:text-[#b91c1c]"
                    title="Supprimer"
                    data-testid={`delete-example-${it.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
